import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTimeTracking } from '../contexts/TimeTrackingContext';
import {
    LayoutDashboard,
    CheckSquare,
    Clock,
    FolderKanban,
    Palmtree,
    Wrench,
    KeyRound,
    StickyNote,
    Bell,
    Settings,
    LogOut,
    Sun,
    Moon,
    Monitor,
    Menu,
    X,
    ChevronRight,
    User,
    Users,
    Trophy,
    Plus,
    ExternalLink,
    Trash2,
    Link2,
    Edit,
    MessageCircle,
    Ticket,
    Shield,
    Calendar,
    UsersRound,
    FileText,
    Smile,
    Library,
    Instagram,
    Contact,
    Activity
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Team-Bereich: Kunden, Content Bibliothek und Social Faces leben jetzt in den
// eigenen Tools (Dropdown neben dem Logo: Kunden -> /kunden/, Social Faces -> /socialfaces/)
const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard_view' },
    { path: '/calendar', icon: Calendar, label: 'Kalender', permission: 'calendar_view' },
    { path: '/tasks', icon: CheckSquare, label: 'Tasks', permission: 'tasks_view' },
    { path: '/time', icon: Clock, label: 'Zeiten', permission: 'time_view' },
    { path: '/projects', icon: FolderKanban, label: 'Projekte', permission: 'projects_view' },
    { path: '/team', icon: UsersRound, label: 'Team Hub', permission: 'team_view' },
    { path: '/forms', icon: FileText, label: 'Formulare', permission: 'forms_view' },
    { path: '/influencers', icon: Instagram, label: 'Influencer', permission: 'influencers_view' },
    { path: '/communications', icon: MessageCircle, label: 'Kommunikation', permission: 'communications_view' },
    { path: '/contacts', icon: Contact, label: 'Kontakte', permission: 'contacts_view' },
    { path: '/challenges', icon: Trophy, label: 'Challenges', permission: 'challenges_view' },
    { path: '/benefits', icon: Ticket, label: 'Vorteilspass', permission: 'benefits_view' },
    { path: '/chat', icon: MessageCircle, label: 'Chat', permission: 'chat_view' },
    { path: '/vacation', icon: Palmtree, label: 'Urlaub', permission: 'vacation_view' },
    { path: '/access', icon: KeyRound, label: 'Zugänge', permission: 'passwords_view' },
    { path: '/notes', icon: StickyNote, label: 'Notizen', permission: 'notes_view' },
];

// Tool-Switcher: die drei Systeme der Fusion
const TOOLS = [
    { key: 'team', label: 'Team', href: '/', icon: UsersRound, description: 'AgencyOS - Kalender, Tasks, Zeiten' },
    { key: 'kunden', label: 'Kunden', href: '/kunden/', icon: Users, description: 'PlanFlow - Kunden, Content, Reportings' },
    { key: 'socialfaces', label: 'Social Faces', href: '/socialfaces/', icon: Smile, description: 'Shootings, Abrechnung, Bewerbungen' },
];

// Default permissions for roles (frontend fallback)
const DEFAULT_PERMISSIONS = {
    chef: { dashboard_view: true, tasks_view: true, time_view: true, projects_view: true, customers_view: true, team_view: true, calendar_view: true, forms_view: true, content_library_view: true, influencers_view: true, communications_view: true, contacts_view: true, social_faces_view: true, challenges_view: true, benefits_view: true, chat_view: true, vacation_view: true, passwords_view: true, notes_view: true },
    account_manager: { dashboard_view: true, tasks_view: true, time_view: true, projects_view: true, customers_view: true, team_view: true, calendar_view: true, forms_view: true, content_library_view: true, influencers_view: true, communications_view: true, contacts_view: true, social_faces_view: true, challenges_view: true, benefits_view: true, chat_view: true, vacation_view: true, passwords_view: true, notes_view: true },
    buchhaltung: { dashboard_view: true, tasks_view: true, time_view: true, projects_view: true, customers_view: true, team_view: true, calendar_view: true, forms_view: true, content_library_view: true, influencers_view: true, communications_view: true, contacts_view: true, social_faces_view: false, challenges_view: true, benefits_view: true, chat_view: true, vacation_view: true, passwords_view: true, notes_view: true },
    mitarbeiter: { dashboard_view: true, tasks_view: true, time_view: true, projects_view: true, customers_view: true, team_view: true, calendar_view: true, forms_view: true, content_library_view: true, influencers_view: true, communications_view: true, contacts_view: true, social_faces_view: false, challenges_view: true, benefits_view: true, chat_view: true, vacation_view: true, passwords_view: true, notes_view: true },
    influencer: { dashboard_view: false, tasks_view: false, time_view: false, projects_view: false, customers_view: false, team_view: false, calendar_view: false, forms_view: false, content_library_view: false, influencers_view: false, communications_view: false, contacts_view: false, social_faces_view: false, challenges_view: false, benefits_view: true, chat_view: false, vacation_view: false, passwords_view: false, notes_view: false },
    freelancer: { dashboard_view: true, tasks_view: true, time_view: false, projects_view: true, customers_view: true, team_view: true, calendar_view: true, forms_view: true, content_library_view: true, influencers_view: true, communications_view: true, contacts_view: true, social_faces_view: false, challenges_view: true, benefits_view: true, chat_view: true, vacation_view: false, passwords_view: true, notes_view: true },
};

const Sidebar = () => {
    const { user, logout, api } = useAuth();
    const { theme, resolvedTheme, toggleTheme } = useTheme();
    const { isTracking, formattedTime, activeTaskId } = useTimeTracking();
    const location = useLocation();
    // Bewerbungen & Verwaltung lebt technisch im Team-Frontend, gehoert aber zum Social-Faces-Tool
    const onSocialFacesAdmin = location.pathname.startsWith('/social-faces-admin');
    const currentToolKey = onSocialFacesAdmin ? 'socialfaces' : 'team';
    const navigate = useNavigate();
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);
    const [customMenuItems, setCustomMenuItems] = useState([]);
    const [showAddMenuModal, setShowAddMenuModal] = useState(false);
    const [showManageLinksModal, setShowManageLinksModal] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [newMenuItem, setNewMenuItem] = useState({ title: '', url: '', icon: '🔗' });
    const [systemSettings, setSystemSettings] = useState(null);

    // Fetch system settings for logo
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/system/settings');
                setSystemSettings(response.data);
            } catch (error) {
                console.log('Using default settings');
            }
        };
        fetchSettings();
    }, [api]);

    // Fetch custom menu items
    useEffect(() => {
        const fetchMenuItems = async () => {
            try {
                const response = await api.get('/users/me/menu-items');
                setCustomMenuItems(response.data);
            } catch (error) {
                console.error('Failed to fetch menu items:', error);
            }
        };
        fetchMenuItems();
    }, [api]);

    // Fetch chat unread count
    useEffect(() => {
        const fetchChatUnread = async () => {
            try {
                const response = await api.get('/chat/unread-count');
                setChatUnreadCount(response.data.unread_count);
            } catch (error) {
                console.error('Failed to fetch chat unread count:', error);
            }
        };
        fetchChatUnread();
        const interval = setInterval(fetchChatUnread, 10000);
        return () => clearInterval(interval);
    }, [api]);

    const handleAddMenuItem = async () => {
        if (!newMenuItem.title || !newMenuItem.url) return;
        try {
            const response = await api.post('/users/me/menu-items', newMenuItem);
            setCustomMenuItems([...customMenuItems, response.data]);
            setShowAddMenuModal(false);
            setNewMenuItem({ title: '', url: '', icon: '🔗' });
            toast.success('Link hinzugefügt');
        } catch (error) {
            toast.error('Fehler beim Hinzufügen');
        }
    };

    const handleDeleteMenuItem = async (itemId) => {
        try {
            await api.delete(`/users/me/menu-items/${itemId}`);
            setCustomMenuItems(customMenuItems.filter(item => item.id !== itemId));
            toast.success('Link entfernt');
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleEditMenuItem = (item) => {
        setEditingLink({ ...item });
    };

    const handleSaveEdit = async () => {
        if (!editingLink) return;
        try {
            await api.put(`/users/me/menu-items/${editingLink.id}`, {
                title: editingLink.title,
                url: editingLink.url,
                icon: editingLink.icon
            });
            setCustomMenuItems(customMenuItems.map(item => 
                item.id === editingLink.id ? editingLink : item
            ));
            setEditingLink(null);
            toast.success('Link aktualisiert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const moveLink = async (index, direction) => {
        const newItems = [...customMenuItems];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= newItems.length) return;
        [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
        setCustomMenuItems(newItems);
        // Persist order to backend
        try {
            await api.put('/users/me/menu-items', newItems);
        } catch (error) {
            console.error('Failed to save order:', error);
        }
    };

    // Fetch unread notifications count
    React.useEffect(() => {
        const fetchUnread = async () => {
            try {
                const response = await api.get('/notifications/unread-count');
                setUnreadCount(response.data.count);
            } catch (error) {
                console.error('Failed to fetch unread count:', error);
            }
        };
        
        fetchUnread();
        const interval = setInterval(fetchUnread, 30000);
        return () => clearInterval(interval);
    }, [api]);

    const handleLogout = async () => {
        try {
            const dashResponse = await api.get('/dashboard');
            const vacationTomorrow = dashResponse.data.special_events?.vacation_tomorrow;
            logout(true, vacationTomorrow);
        } catch {
            logout(true, false);
        }
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    return (
        <>
            {/* Mobile Header Bar - Fixed at top with safe area for notch */}
            <div 
                className="fixed top-0 left-0 right-0 bg-card/95 backdrop-blur-xl border-b border-border/50 z-50 md:hidden flex items-center px-2"
                style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(56px + env(safe-area-inset-top))' }}
            >
                {/* Left: Burger + Logo (compact) */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!isMobileOpen && (
                        <button
                            onClick={() => setIsMobileOpen(true)}
                            className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
                            data-testid="mobile-menu-toggle"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    )}
                    {!isMobileOpen && (
                        <Link
                            to="/"
                            aria-label="Zum Dashboard"
                            data-testid="mobile-logo-home-link"
                            className={`w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden ${!systemSettings?.logo_url ? 'bg-primary' : ''}`}
                        >
                            {systemSettings?.logo_url ? (
                                <img src={systemSettings.logo_url} alt="Logo" className="w-full h-full object-contain" />
                            ) : (
                                <span className="text-white font-bold text-xs">A</span>
                            )}
                        </Link>
                    )}
                </div>
                
                {/* Center: Timer if tracking - only shown when tracking, otherwise empty for spacing */}
                <div className="flex-1 flex justify-center items-center min-w-0 px-1">
                    {isTracking && !isMobileOpen && (
                        <div className="flex items-center gap-1.5 text-primary">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                            <span className="text-xs font-mono font-semibold">{formattedTime}</span>
                        </div>
                    )}
                </div>
                
                {/* Right: Space reserved for HeaderBubble icons */}
                <div className="w-[148px] flex-shrink-0" />
            </div>

            {/* Mobile overlay */}
            <AnimatePresence>
                {isMobileOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[55] md:hidden"
                        onClick={() => setIsMobileOpen(false)}
                    />
                )}
            </AnimatePresence>

            {/* Sidebar */}
            <motion.aside
                className={cn(
                    "fixed left-0 top-0 h-screen w-64 bg-card/95 backdrop-blur-2xl border-r border-border/50 flex flex-col",
                    "md:translate-x-0 transition-transform duration-300",
                    "md:z-40",
                    isMobileOpen ? "translate-x-0 z-[60]" : "-translate-x-full md:translate-x-0"
                )}
                style={{ paddingTop: 'env(safe-area-inset-top)' }}
                data-testid="sidebar"
            >
                {/* Logo/Brand - with close button on mobile */}
                <div className="p-4 border-b border-border/50">
                    <div className="flex items-center justify-between">
                        {/* Klick auf Logo/Name -> zurueck zum Team-Dashboard */}
                        <Link
                            to="/"
                            onClick={() => setIsMobileOpen(false)}
                            aria-label="Zum Dashboard"
                            data-testid="sidebar-logo-home-link"
                            className="flex items-center gap-3 rounded-xl hover:opacity-80 transition-opacity"
                        >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden ${!systemSettings?.logo_url ? 'bg-primary' : ''}`}>
                                {systemSettings?.logo_url ? (
                                    <img src={systemSettings.logo_url} alt="Logo" className="w-full h-full object-contain" />
                                ) : (
                                    <span className="text-white font-bold">A</span>
                                )}
                            </div>
                            <div>
                                <h1 className="font-semibold text-base tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    {systemSettings?.company_name || 'AgencyOS'}
                                </h1>
                                <p className="text-[10px] text-muted-foreground">AgencyOS</p>
                            </div>
                        </Link>
                        {/* Close button on mobile - right side */}
                        <button
                            onClick={() => setIsMobileOpen(false)}
                            className="p-2 rounded-xl hover:bg-muted/50 transition-colors md:hidden"
                            data-testid="mobile-menu-close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tool-Switcher: Team / Kunden / Social Faces */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="mt-3 w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-primary/10 border border-primary/30 text-primary font-semibold text-sm hover:bg-primary/20 transition-colors"
                                data-testid="tool-switcher"
                            >
                                <span className="flex items-center gap-2">
                                    {onSocialFacesAdmin ? <Smile className="w-4 h-4" /> : <UsersRound className="w-4 h-4" />}
                                    {onSocialFacesAdmin ? 'Social Faces' : 'Team'}
                                </span>
                                <ChevronRight className="w-4 h-4 rotate-90" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-60">
                            {TOOLS.map((tool) => (
                                <DropdownMenuItem
                                    key={tool.key}
                                    onClick={() => {
                                        if (tool.key === 'team' && onSocialFacesAdmin) navigate('/');
                                        else if (tool.key !== 'team') window.location.href = tool.href;
                                    }}
                                    className={cn('py-2.5 px-3 cursor-pointer', tool.key === currentToolKey && 'bg-primary/10')}
                                >
                                    <div className="flex items-center gap-3">
                                        <tool.icon className={cn('w-5 h-5', tool.key === currentToolKey ? 'text-primary' : 'text-muted-foreground')} />
                                        <div>
                                            <div className={cn('font-semibold text-sm', tool.key === currentToolKey && 'text-primary')}>{tool.label}</div>
                                            <div className="text-[11px] text-muted-foreground">{tool.description}</div>
                                        </div>
                                    </div>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {onSocialFacesAdmin && (
                    <a
                        href="/socialfaces/admin"
                        className="mx-4 mt-3 flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-primary bg-primary/5 border border-primary/20 hover:bg-primary/10"
                        data-testid="back-to-socialfaces"
                    >
                        <ChevronRight className="w-4 h-4 rotate-180" />
                        Zurück zu Social Faces
                    </a>
                )}

                {/* Active time tracking indicator */}
                {isTracking && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mx-4 mt-4 p-3 rounded-xl bg-primary/10 border border-primary/30"
                    >
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                            <span className="text-sm font-medium text-primary">Zeit läuft</span>
                        </div>
                        <p className="text-2xl font-mono font-semibold text-primary mt-1 timer-display">
                            {formattedTime}
                        </p>
                        {activeTaskId && (
                            <button
                                onClick={() => {
                                    navigate(`/tasks?active=${activeTaskId}`);
                                    setIsMobileOpen(false);
                                }}
                                className="text-xs text-primary/70 hover:text-primary mt-1 flex items-center gap-1"
                            >
                                Zum Task <ChevronRight className="w-3 h-3" />
                            </button>
                        )}
                    </motion.div>
                )}

                {/* Navigation */}
                <nav className="flex-1 p-4 overflow-y-auto">
                    <ul className="space-y-1">
                        {navItems.filter((item) => {
                            // Get user permissions - use custom permissions if set, otherwise fall back to role defaults
                            const userRole = (user?.role || '').toLowerCase().replace(/\s+/g, '_');
                            const rolePermissions = DEFAULT_PERMISSIONS[userRole] || DEFAULT_PERMISSIONS[user?.role] || DEFAULT_PERMISSIONS.mitarbeiter;
                            const userPermissions = user?.permissions 
                                ? { ...rolePermissions, ...user.permissions }  // Merge: role defaults + user overrides
                                : rolePermissions;
                            // Check if user has permission for this nav item
                            if (userPermissions[item.permission] === false) return false;
                            // Check if challenges module is disabled
                            if (item.path === '/challenges' && systemSettings?.challenges_enabled === false) return false;
                            // Check if chat module is disabled
                            if (item.path === '/chat' && systemSettings?.chat_enabled === false) return false;
                            // Check if projects module is disabled
                            if (item.path === '/projects' && systemSettings?.projects_enabled === false) return false;
                            return true;
                        }).map((item) => (
                            <li key={item.path}>
                                <NavLink
                                    to={item.path}
                                    onClick={() => setIsMobileOpen(false)}
                                    className={({ isActive }) => cn(
                                        "flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground transition-all duration-200 relative",
                                        "hover:bg-muted/50 hover:text-foreground",
                                        isActive && "bg-primary/10 text-primary font-medium"
                                    )}
                                    data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span>{item.label}</span>
                                    {item.path === '/chat' && chatUnreadCount > 0 && (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                                            {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                                        </span>
                                    )}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {/* Custom Menu Items */}
                    {(customMenuItems.length > 0 || true) && (
                        <div className="mt-4 pt-4 border-t border-border/30">
                            <div className="flex items-center justify-between px-4 mb-2">
                                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Meine Links</span>
                                <div className="flex items-center gap-1">
                                    {customMenuItems.length > 0 && (
                                        <button
                                            onClick={() => setShowManageLinksModal(true)}
                                            className="p-1 rounded hover:bg-muted/50 transition-colors"
                                            title="Links verwalten"
                                        >
                                            <Settings className="w-4 h-4 text-muted-foreground" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowAddMenuModal(true)}
                                        className="p-1 rounded hover:bg-muted/50 transition-colors"
                                        title="Link hinzufügen"
                                    >
                                        <Plus className="w-4 h-4 text-muted-foreground" />
                                    </button>
                                </div>
                            </div>
                            <ul className="space-y-1">
                                {customMenuItems.map((item) => {
                                    // Ensure URL has protocol
                                    let url = item.url;
                                    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                                        url = 'https://' + url;
                                    }
                                    return (
                                        <li key={item.id} className="relative">
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-muted-foreground transition-all duration-200 hover:bg-muted/50 hover:text-foreground"
                                            >
                                                <span className="text-base">{item.icon}</span>
                                                <span className="flex-1 truncate text-sm">{item.title}</span>
                                                <ExternalLink className="w-3.5 h-3.5 opacity-50" />
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                    
                    {/* Personal Activity Log - visible to all users */}
                    <div className="mt-4 pt-4 border-t border-border/30">
                        <ul className="space-y-1">
                            <li>
                                <NavLink
                                    to="/meine-aktivitaet"
                                    onClick={() => setIsMobileOpen(false)}
                                    className={({ isActive }) => cn(
                                        "flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground transition-all duration-200",
                                        "hover:bg-muted/50 hover:text-foreground",
                                        isActive && "bg-primary/10 text-primary font-medium"
                                    )}
                                    data-testid="nav-my-activity"
                                >
                                    <Activity className="w-5 h-5" />
                                    <span>Meine Aktivität</span>
                                </NavLink>
                            </li>
                        </ul>
                    </div>

                    {/* Admin Section - Only visible for chef role */}
                    {user?.role === 'chef' && (
                        <div className="mt-4 pt-4 border-t border-border/30">
                            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider px-4 mb-2 block">Admin</span>
                            <ul className="space-y-1">
                                <li>
                                    <NavLink
                                        to="/audit-log"
                                        onClick={() => setIsMobileOpen(false)}
                                        className={({ isActive }) => cn(
                                            "flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground transition-all duration-200",
                                            "hover:bg-muted/50 hover:text-foreground",
                                            isActive && "bg-amber-500/10 text-amber-600 font-medium"
                                        )}
                                        data-testid="nav-audit-log"
                                    >
                                        <Shield className="w-5 h-5" />
                                        <span>Audit-Log</span>
                                    </NavLink>
                                </li>
                            </ul>
                        </div>
                    )}
                    
                    {/* Copyright Footer */}
                    <div className="mt-auto pt-4 px-4 pb-4 border-t border-border/30">
                        <a 
                            href="https://www.tgosm.io" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[10px] text-muted-foreground/60 hover:text-muted-foreground transition-colors block text-center"
                        >
                            Copyright © 2026 TGOSM | Made with ♥ in Vienna
                        </a>
                    </div>
                </nav>
            </motion.aside>

            {/* Add Menu Item Modal - rendered outside sidebar with portal */}
            <Dialog open={showAddMenuModal} onOpenChange={(open) => {
                setShowAddMenuModal(open);
                // Don't close mobile menu when modal closes
            }}>
                <DialogContent className="max-w-sm z-[200]" onPointerDownOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Link2 className="w-5 h-5" />
                            Link hinzufügen
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Emoji/Icon</Label>
                            <div className="flex gap-2 mt-2 flex-wrap">
                                {['🔗', '📁', '💬', '📧', '📊', '🎨', '🔧', '📝', '🌐', '⭐'].map(emoji => (
                                    <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => setNewMenuItem({...newMenuItem, icon: emoji})}
                                        className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                                            newMenuItem.icon === emoji ? 'bg-primary/20 ring-2 ring-primary' : 'bg-muted/50 hover:bg-muted'
                                        }`}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>Name *</Label>
                            <Input
                                value={newMenuItem.title}
                                onChange={(e) => setNewMenuItem({...newMenuItem, title: e.target.value})}
                                placeholder="z.B. Slack, Google Drive..."
                            />
                        </div>
                        <div>
                            <Label>URL *</Label>
                            <Input
                                value={newMenuItem.url}
                                onChange={(e) => setNewMenuItem({...newMenuItem, url: e.target.value})}
                                placeholder="https://..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddMenuModal(false)}>Abbrechen</Button>
                        <Button onClick={handleAddMenuItem} disabled={!newMenuItem.title || !newMenuItem.url}>
                            Hinzufügen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Manage Links Modal */}
            <Dialog open={showManageLinksModal} onOpenChange={setShowManageLinksModal}>
                <DialogContent className="max-w-md z-[200]" onPointerDownOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Links verwalten
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                        {customMenuItems.map((item, index) => (
                            <div key={item.id} className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 group">
                                {editingLink?.id === item.id ? (
                                    // Edit mode
                                    <div className="flex-1 space-y-2">
                                        <div className="flex gap-2">
                                            <div className="flex gap-1 flex-wrap">
                                                {['🔗', '📁', '💬', '📧', '📊', '🎨', '🔧', '📝', '🌐', '⭐'].map(emoji => (
                                                    <button
                                                        key={emoji}
                                                        type="button"
                                                        onClick={() => setEditingLink({...editingLink, icon: emoji})}
                                                        className={`w-8 h-8 rounded text-lg flex items-center justify-center ${
                                                            editingLink.icon === emoji ? 'bg-primary/20 ring-1 ring-primary' : 'bg-muted/50'
                                                        }`}
                                                    >
                                                        {emoji}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <Input
                                            value={editingLink.title}
                                            onChange={(e) => setEditingLink({...editingLink, title: e.target.value})}
                                            placeholder="Name"
                                            className="h-8"
                                        />
                                        <Input
                                            value={editingLink.url}
                                            onChange={(e) => setEditingLink({...editingLink, url: e.target.value})}
                                            placeholder="URL"
                                            className="h-8"
                                        />
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => setEditingLink(null)}>
                                                Abbrechen
                                            </Button>
                                            <Button size="sm" onClick={handleSaveEdit}>
                                                Speichern
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    // View mode
                                    <>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => moveLink(index, 'up')}
                                                disabled={index === 0}
                                                className="p-1 rounded hover:bg-muted disabled:opacity-30"
                                            >
                                                <ChevronRight className="w-4 h-4 -rotate-90" />
                                            </button>
                                            <button
                                                onClick={() => moveLink(index, 'down')}
                                                disabled={index === customMenuItems.length - 1}
                                                className="p-1 rounded hover:bg-muted disabled:opacity-30"
                                            >
                                                <ChevronRight className="w-4 h-4 rotate-90" />
                                            </button>
                                        </div>
                                        <span className="text-xl">{item.icon}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{item.title}</p>
                                            <p className="text-xs text-muted-foreground truncate">{item.url}</p>
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => handleEditMenuItem(item)}
                                                className="p-1.5 rounded hover:bg-muted transition-colors"
                                                title="Bearbeiten"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteMenuItem(item.id)}
                                                className="p-1.5 rounded hover:bg-destructive/20 text-destructive transition-colors"
                                                title="Löschen"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                        {customMenuItems.length === 0 && (
                            <p className="text-center text-muted-foreground py-4">Keine Links vorhanden</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setShowManageLinksModal(false)}>Schließen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Sidebar;
